import {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react'
import {createBowlMutation} from './bowlMutation.js'
import {getSessionGeneration} from './sessionGeneration.js'
import {useSessionGeneration} from './useSessionGeneration.js'

const BowlMutationContext = createContext(null)
export function BowlMutationProvider({meta, children}) {
  const generation = useSessionGeneration()
  const [notice, setNotice] = useState('')
  const currentMeta = useRef(meta)
  currentMeta.current = meta
  const control = useRef(null)
  if (!control.current) control.current = createBowlMutation({getMeta: () => currentMeta.current, onNotice:setNotice})
  useEffect(() => {
    setNotice('')
    control.current = createBowlMutation({getMeta: () => currentMeta.current, onNotice:setNotice})
    const own = control.current
    return () => own.dispose()
  }, [generation])
  const run = useCallback((send, body, context) => control.current.run(send, body, context), [])
  return <BowlMutationContext.Provider value={run}>{children}{notice && <p role="status" aria-live="polite">{notice}</p>}</BowlMutationContext.Provider>
}
export function useBowlMutation() {
  const run = useContext(BowlMutationContext)
  if (!run) throw new Error('A fresh bowl action context is required.')
  return run
}

// Blocks late reads/writes from an unmounted panel or a prior login. Reads
// still use AbortController to exclude older responses in the same session.
export function useFencedState(initial) {
  const generation = useSessionGeneration()
  const seed = useRef(null)
  if (!seed.current) seed.current = {value: typeof initial === 'function' ? initial() : initial}
  const [snapshot, update] = useState(() => ({generation, value: seed.current.value}))
  const active = useRef(false)
  useEffect(() => { active.current = true; return () => { active.current = false } }, [])
  const setState = useCallback(value => {
    if (!active.current || generation !== getSessionGeneration()) return
    update(previous => ({generation, value: typeof value === 'function'
      ? value(previous.generation === generation ? previous.value : seed.current.value) : value}))
  }, [generation])
  return [snapshot.generation === generation ? snapshot.value : seed.current.value, setState]
}
